export interface II18nAlternate {
  hreflang: string;
  href: string;
}

export interface IRoutePair {
  en: string;
  fr: string;
  indexable?: boolean;
}

// Master list of EN ↔ FR route mappings.
// `indexable: true` means the page is in the sitemap and should emit hreflang/canonical alternates.
// Bare base prefixes (e.g. "/models", "/vcard") exist to support longest-prefix matching for dynamic
// child routes (e.g. /models/SomeNewModel resolves via /models ↔ /fr/modeles).
export const ROUTE_PAIRS: readonly IRoutePair[] = [
  // Indexable
  { en: "/", fr: "/fr/", indexable: true },
  { en: "/projects", fr: "/fr/projets", indexable: true },
  { en: "/models/overview", fr: "/fr/modeles/overview", indexable: true },
  { en: "/models/PakMonaco", fr: "/fr/modeles/PakMonaco", indexable: true },
  { en: "/models/PakSamara", fr: "/fr/modeles/PakSamara", indexable: true },
  { en: "/models/PakAlps", fr: "/fr/modeles/PakAlps", indexable: true },
  { en: "/models/PakTokyo", fr: "/fr/modeles/PakTokyo", indexable: true },
  { en: "/models/PakShiraz", fr: "/fr/modeles/PakShiraz", indexable: true },
  {
    en: "/models/PakStockholm",
    fr: "/fr/modeles/PakStockholm",
    indexable: true,
  },
  { en: "/models/PakNewYork", fr: "/fr/modeles/PakNewYork", indexable: true },
  { en: "/models/PakMontreal", fr: "/fr/modeles/PakMontreal", indexable: true },
  { en: "/shop", fr: "/fr/commandez", indexable: true },
  { en: "/shop/pakvert", fr: "/fr/commandez/pakvert", indexable: true },
  { en: "/the-technology", fr: "/fr/la-technologie", indexable: true },
  { en: "/blog", fr: "/fr/blog", indexable: true },
  {
    en: "/multi-storey-buildings",
    fr: "/fr/batiments-multietages",
    indexable: true,
  },
  {
    en: "/multi-residential-kits",
    fr: "/fr/ensembles-multiresidentiels",
    indexable: true,
  },
  { en: "/contact-us", fr: "/fr/contactez-nous", indexable: true },
  { en: "/media-coverage", fr: "/fr/couverture-mediatique", indexable: true },
  { en: "/awards", fr: "/fr/prix", indexable: true },
  { en: "/who-we-are", fr: "/fr/qui-nous-sommes", indexable: true },
  {
    en: "/privacy-policy",
    fr: "/fr/politique-de-confidentialite",
    indexable: true,
  },
  { en: "/terms-of-service", fr: "/fr/conditions-de-service", indexable: true },
  {
    en: "/refund-policy",
    fr: "/fr/politique-de-remboursement",
    indexable: true,
  },
  {
    en: "/reservation-agreement",
    fr: "/fr/contrat-de-reservation",
    indexable: true,
  },
  {
    en: "/community-partnerships",
    fr: "/fr/partenariats-communautaires",
    indexable: true,
  },
  {
    en: "/releases/2026-10-20",
    fr: "/fr/communiques/2026-10-20",
    indexable: true,
  },
  {
    en: "/releases/2026-05-08",
    fr: "/fr/communiques/2026-05-08",
    indexable: true,
  },

  // Bare base prefixes for dynamic children (not indexable on their own)
  { en: "/models", fr: "/fr/modeles" },
  { en: "/vcard", fr: "/fr/vcard" },

  // Non-indexable utility / flow pages
  { en: "/cart", fr: "/fr/panier" },
  { en: "/checkout", fr: "/fr/caisse" },
  { en: "/success", fr: "/fr/succes" },
  { en: "/success-reserve", fr: "/fr/success-reserve" },
  { en: "/error", fr: "/fr/erreur" },
  { en: "/schedule-a-call", fr: "/fr/planifiez-un-appel" },
  { en: "/pay", fr: "/fr/payer" },
  { en: "/manage-preferences", fr: "/fr/gerer-preference" },

  // Aliases (legacy — should ideally 301 to canonical at the CDN)
  { en: "/our-story", fr: "/fr/notre-histoire" },

  // Routes whose components are currently commented-out in App.tsx but still
  // referenced by t("_route.*") in code (e.g. Installation.component.tsx).
  { en: "/how-it-works", fr: "/fr/comment-ca-marche" },
];

// Localized form of a canonical EN path, e.g. localizePath("/events", "fr")
// -> "/fr/evenements". Reads ROUTE_PAIRS directly rather than going through
// t("_route.*"), because i18n's language is set in a useEffect and is still
// "en" on first paint — which would emit a canonical for a URL that does not
// exist. Falls back to the EN path when the pair is unknown.
export function localizePath(enPath: string, lang: "en" | "fr"): string {
  if (lang === "en") return enPath;
  const pair = ROUTE_PAIRS.find((p) => p.en === enPath);
  return pair ? pair.fr : enPath;
}

export function getAlternatesForPath(
  pathname: string,
): II18nAlternate[] | undefined {
  const pair = ROUTE_PAIRS.find(
    (p) => p.indexable && (p.en === pathname || p.fr === pathname),
  );
  if (!pair) return undefined;
  return [
    { hreflang: "en", href: pair.en },
    { hreflang: "fr", href: pair.fr },
    { hreflang: "x-default", href: pair.en },
  ];
}

interface IPrefixMatch {
  pair: IRoutePair;
  prefix: string;
  isFr: boolean;
}

// Returns the EN/FR counterpart of the given path. Falls back to longest-prefix
// matching so dynamic child routes (e.g. /blog/some-slug) translate correctly.
export function getCounterpartPath(pathname: string): string | undefined {
  const exact = ROUTE_PAIRS.find((p) => p.en === pathname || p.fr === pathname);
  if (exact) return pathname === exact.en ? exact.fr : exact.en;

  const matches = ROUTE_PAIRS.flatMap<IPrefixMatch>((pair) => {
    if (pathname.startsWith(`${pair.en}/`)) {
      return [{ pair, prefix: pair.en, isFr: false }];
    }
    if (pathname.startsWith(`${pair.fr}/`)) {
      return [{ pair, prefix: pair.fr, isFr: true }];
    }
    return [];
  });
  if (!matches.length) return undefined;
  const best = matches.reduce((acc, m) =>
    m.prefix.length > acc.prefix.length ? m : acc,
  );
  const rest = pathname.slice(best.prefix.length);
  const replacement = best.isFr ? best.pair.en : best.pair.fr;
  return replacement + rest;
}

// Derives the `_route` translation namespace from ROUTE_PAIRS for a given language.
// Keys are the first segment of the EN path; values are the first non-"fr" segment
// of the path in the target language. e.g. "models" → "models" (en) / "modeles" (fr).
export function buildRouteSlugBundle(
  lang: "en" | "fr",
): Record<string, string> {
  return ROUTE_PAIRS.reduce<Record<string, string>>((acc, pair) => {
    const enSegs = pair.en.split("/").filter(Boolean);
    if (!enSegs.length) return acc;
    const key = enSegs[0];
    const targetPath = lang === "en" ? pair.en : pair.fr;
    const targetSegs = targetPath.split("/").filter(Boolean);
    const value =
      lang === "fr" && targetSegs[0] === "fr" ? targetSegs[1] : targetSegs[0];
    if (!value) return acc;
    if (acc[key] && acc[key] !== value) {
      // eslint-disable-next-line no-console
      console.warn(
        `[i18nRoutes] Conflicting first-segment mapping for "${key}": "${acc[key]}" vs "${value}"`,
      );
    }
    acc[key] = value;
    return acc;
  }, {});
}
